<template>
    <div v-if="showModal.profileCouldCauseAlarm" class="relative z-10" id="confirm-modal" aria-labelledby="modal-title" role="dialog" aria-modal="true">

        <div class="fixed inset-0 bg-gray-600/50 transition-opacity" aria-hidden="true"></div>

        <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">

                <div class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                    <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div class="sm:flex sm:items-start">
                            <div class="mt-3 text-left sm:mt-0">
                                <h3 class="py-2 text-left text-base font-semibold text-gray-900" id="modal-title" aria-hidden="true">
                                    <span class="lnr icon-warning mr-2 !text-base !font-semibold"></span>
                                    Warning
                                </h3>
                                <p class="text-sm text-gray-500 mt-2">
                                    The following points using this alarm profile would currently trigger an alarm based on the defined thresholds.
                                </p>

                                <ul class="list-disc list-inside text-sm text-gray-500 mt-3 ml-1">
                                    <li v-for="meter in this.metersExceeded" :key="meter">
                                        {{ meter }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                        <button type="button"
                                @click="confirmProfileCouldCauseAlarm()"
                                class="inline-flex w-full justify-center rounded-md bg-yellow-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-500 sm:ml-3 sm:w-auto">Confirm Changes</button>
                        <button type="button"
                                @click="showModal.profileCouldCauseAlarm = false"
                                class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">Go Back</button>
                    </div>

                </div>
            </div>
        </div>

    </div>

    <div    class="mt-8 relative h-full overflow-y-scroll">
        <div    v-if="viewOnly"
                class="absolute top-0 left-0 z-50 w-full h-full"></div>
        <form method="POST"
              @submit.prevent="submitEvent"
              :action="formRoute"
              ref="alarmProfileForm"
        >
            <input name="_method" type="hidden" :value="id === null ? 'POST' : 'PATCH'">
            <input type="hidden" name="_token" :value="csrf"/>
            <!--
              Tailwind UI : Two Column Cards with separate submit actions
              https://tailwindui.com/components/application-ui/forms/form-layouts#component-db11f83176d113e39bf2559da9344b1c
            -->
            <div>
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <div class="px-4 sm:px-0">
                            <span class="text-lg font-medium leading-6 text-gray-900">Alarm Profile</span>
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                Give a unique name that will help others identify this alarm profile.
                            </p>
                        </div>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="shadow sm:rounded-md sm:overflow-hidden">
                            <div class="px-4 py-5 bg-white sm:p-6">
                                <div class="col-span-6">
                                    <label for="name" class="block text-sm font-medium leading-5 text-gray-700">
                                        Alarm Profile Name</label>
                                    <input type="text" id="name"
                                           name="name"
                                           class="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-200 focus:border-blue-300 sm:text-base sm:leading-5"
                                           v-model="data.name"
                                           required
                                    >
                                </div>
                                <fieldset v-if="showPrivateToggle" class="mt-8 ml-0 pl-0">
                                    <legend class="text-base leading-6 font-medium text-gray-900">Custom Alarm Profile
                                        Mode
                                    </legend>
                                    <div class="mt-4">
                                        <div class="flex items-start">
                                            <div class="flex items-center h-5">
                                                <toggle id="private" name="private" v-model:value="data.private"></toggle>
                                            </div>
                                            <div class="ml-3 text-sm leading-5">
                                                <label class="font-medium text-gray-700">Private Mode</label>
                                                <p class="text-gray-500">Disable private mode to make this alarm profile
                                                    re-usable by other points.</p>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>


                                <fieldset v-if="hasSoundDevices" class="mt-8 ml-0 pl-0">
                                    <legend class="text-base leading-6 font-medium text-gray-900">Sound Device Options
                                    </legend>
                                    <div class="mt-4">
                                        <div class="flex items-start">
                                            <div class="flex items-center h-5">
                                                <toggle name="force_push_indicators"
                                                        id="force_push_indicators"
                                                        v-model:value="data.force_push_indicators"
                                                ></toggle>
                                            </div>
                                            <div class="ml-3 text-sm leading-5">
                                                <label class="font-medium text-gray-700">Push Indicators to
                                                    Points</label>
                                                <p class="text-gray-500">
                                                    Toggling on will force the value to send to all
                                                    points. Not toggling will send immediately if points are online.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="hidden sm:block">
                <div class="py-5">
                    <div class="border-t border-gray-200"></div>
                </div>
            </div>

            <div class="mt-10 sm:mt-0">
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <div class="px-4 sm:px-0">
                            <span class="text-lg font-medium leading-6 text-gray-900">Enable Alarms</span>
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                Thresholds set the boundaries for acceptable point values.
                            </p>
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                Offline alarms can be generated when a meter is offline for a specific amount of time.
                            </p>
                        </div>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="shadow overflow-hidden sm:rounded-md">
                            <div class="px-4 py-5 bg-white sm:p-6">
                                <fieldset class="ml-0 pl-0">
                                    <legend class="text-base leading-6 font-medium text-gray-900">Excursion (Threshold)
                                        Alarms
                                    </legend>
                                    <div class="mt-4">
                                        <div class="flex items-start">
                                            <div class="flex items-center h-5">
                                                <toggle id="threshold_alarms_enabled"
                                                        @toggle="inputChanged = true"
                                                        name="threshold_alarms_enabled"
                                                        v-model:value="data.threshold_alarms_enabled"></toggle>
                                            </div>
                                            <div class="ml-3 text-sm leading-5">
                                                <label class="font-medium text-gray-700">Excursion (Threshold)
                                                    Alarms</label>
                                                <p class="text-gray-500">Create alarm when point has an excursion
                                                    outside of the thresholds.</p>
                                            </div>
                                        </div>
                                        <div v-if="data.threshold_alarms_enabled" class="mt-4">
                                            <div class="flex items-start">
                                                <div class="flex items-center h-5">
                                                    <toggle id="threshold_resolve_enabled"
                                                            name="threshold_resolve_enabled"
                                                            v-model:value="data.threshold_resolve_enabled"></toggle>
                                                </div>
                                                <div class="ml-3 text-sm leading-5">
                                                    <label class="font-medium text-gray-700">Threshold Automatic
                                                        Resolution</label>
                                                    <p class="text-gray-500">Automatically resolve alarms when points
                                                        are back within the 'acceptable' thresholds.</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="data.threshold_alarms_enabled && data.threshold_resolve_enabled"
                                             class="pl-12 ml-1 mt-2">
                                            <label for="high_alarm_time"
                                                   class="block text-sm font-medium leading-5 text-gray-700">Threshold
                                                Automatic Resolution Buffer Time</label>
                                            <input type="text"
                                                   placeholder="HH:MM"
                                                   name="threshold_resolve_time"
                                                   id="threshold_resolve_time"
                                                   :value="thresholdResolveTimeReadable"
                                                   class="border p-2 text-center rounded-lg w-full"
                                                   :class="instantAlarming ? 'sm:w-1/3' : 'sm:w-1/2'"
                                                   @focus="openModal">
                                        </div>

                                    </div>
                                </fieldset>
                                <fieldset class="mt-4 ml-0 pl-0">
                                    <legend class="text-base leading-6 font-medium text-gray-900">Offline Alarms
                                    </legend>
                                    <div class="mt-4">
                                        <div class="flex items-start">
                                            <div class="flex items-center h-5">
                                                <toggle     id="offline_alarms_enabled"
                                                            v-model:value="data.offline_alarms_enabled"
                                                            name="offline_alarms_enabled"
                                                            @click="updateEnableOfflineAlarmThreshold(data.offline_alarms_enabled)"
                                                ></toggle>
                                            </div>
                                            <div class="ml-3 text-sm leading-5">
                                                <label class="font-medium text-gray-700">Offline Alarms</label>
                                                <p class="text-gray-500">Create alarm when meter goes offline.</p>
                                            </div>
                                        </div>
                                        <div v-if="data.offline_alarms_enabled" class="mt-4">
                                            <div class="flex items-start">
                                                <div class="flex items-center h-5">
                                                    <toggle     id="offline_resolve_enabled"
                                                                v-model:value="data.offline_resolve_enabled"
                                                                name="offline_resolve_enabled">
                                                    ></toggle>
                                                </div>
                                                <div class="ml-3 text-sm leading-5">
                                                    <label class="font-medium text-gray-700">Offline Automatic
                                                        Resolution</label>
                                                    <p class="text-gray-500">Automatically resolve alarms when points
                                                        are back online.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>

                                <!-- Alarm Generation Schedule -->
                                <fieldset class="mt-8 ml-0 pl-0">
                                    <legend class="text-base leading-6 font-medium text-gray-900">Alarm Generation Schedule</legend>
                                    <div class="mt-4">
                                        <div class="flex items-start">
                                            <div class="flex items-center h-5">
                                                <toggle id="alarm_generation_schedule_enabled"
                                                        v-model:value="alarmGenerationScheduleEnabled"
                                                        name="alarm_generation_schedule_enabled"></toggle>
                                            </div>
                                            <div class="ml-3 text-sm leading-5">
                                                <label class="font-medium text-gray-700">Limit alarm generation to a schedule</label>
                                                <p class="text-gray-500">
                                                    During the off-window, this profile will not generate new threshold
                                                    or offline alarms — but any alarms already active will continue to
                                                    notify and auto-resolve normally. Use this to silence alarm creation
                                                    during predictable off-hours (e.g., closed labs, recurring
                                                    maintenance windows). Times use your site's timezone
                                                    ({{ timezoneAbbreviated }}). To monitor overnight
                                                    (e.g., 5 PM to 8 AM), set Start later than End.
                                                </p>
                                            </div>
                                        </div>
                                        <div v-if="alarmGenerationScheduleEnabled" class="mt-4 ml-12">
                                            <!-- Daily / Weekday / Custom mode switch -->
                                            <div class="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
                                                <button :class="alarmGenerationScheduleMode === 'daily' ? 'bg-white text-gray-900 shadow' : 'text-gray-500'"
                                                        class="px-4 py-1 text-sm font-medium rounded-md focus:outline-none"
                                                        type="button"
                                                        @click="alarmGenerationScheduleMode = 'daily'">Daily</button>
                                                <button :class="alarmGenerationScheduleMode === 'weekday' ? 'bg-white text-gray-900 shadow' : 'text-gray-500'"
                                                        class="px-4 py-1 text-sm font-medium rounded-md focus:outline-none"
                                                        type="button"
                                                        @click="alarmGenerationScheduleMode = 'weekday'">Weekday</button>
                                                <button :class="alarmGenerationScheduleMode === 'custom' ? 'bg-white text-gray-900 shadow' : 'text-gray-500'"
                                                        class="px-4 py-1 text-sm font-medium rounded-md focus:outline-none"
                                                        type="button"
                                                        @click="alarmGenerationScheduleMode = 'custom'">Custom</button>
                                            </div>
                                            <p v-if="alarmGenerationScheduleMode === 'weekday'" class="mt-2 text-sm text-gray-500">
                                                Applies Monday–Friday; no alarms are generated on weekends.
                                            </p>
                                            <p v-if="alarmGenerationScheduleMode === 'custom'" class="mt-2 text-sm text-gray-500">
                                                Set independent time windows for each day; days left off generate no alarms.
                                                A window whose end time is earlier than its start continues past midnight into the next day.
                                            </p>
                                        </div>
                                        <!-- Daily / Weekday: single shared window -->
                                        <div v-if="alarmGenerationScheduleEnabled && alarmGenerationScheduleMode !== 'custom'" class="mt-4 grid grid-cols-2 gap-4 sm:w-2/3 ml-12">
                                            <div>
                                                <label class="block text-sm font-medium leading-5 text-gray-700"
                                                       for="alarm_generation_schedule_start">Start</label>
                                                <input id="alarm_generation_schedule_start"
                                                       :value="alarmGenerationScheduleStartReadable"
                                                       class="mt-1 border p-2 w-full text-center rounded-lg cursor-pointer"
                                                       readonly
                                                       type="text"
                                                       @focus="openAlarmGenerationSchedulePicker('start')">
                                            </div>
                                            <div>
                                                <label class="block text-sm font-medium leading-5 text-gray-700"
                                                       for="alarm_generation_schedule_end">End</label>
                                                <input id="alarm_generation_schedule_end"
                                                       :value="alarmGenerationScheduleEndReadable"
                                                       class="mt-1 border p-2 w-full text-center rounded-lg cursor-pointer"
                                                       readonly
                                                       type="text"
                                                       @focus="openAlarmGenerationSchedulePicker('end')">
                                            </div>
                                        </div>
                                        <!-- Custom: per-day windows -->
                                        <div v-if="alarmGenerationScheduleEnabled && alarmGenerationScheduleMode === 'custom'" class="mt-4 ml-12 sm:w-2/3">
                                            <div v-for="day in alarmGenerationScheduleDays" :key="day.index"
                                                 class="border-t border-gray-200 py-3">
                                                <div class="flex items-center justify-between">
                                                    <div class="flex items-center h-5">
                                                        <toggle :id="`alarm_generation_schedule_custom_${day.index}`"
                                                                v-model:value="alarmGenerationScheduleCustom[day.index].enabled"
                                                                :name="`alarm_generation_schedule_custom_${day.index}`"
                                                                @update:value="toggleCustomDay(day.index)"></toggle>
                                                        <label class="ml-3 font-medium text-gray-700">{{ day.label }}</label>
                                                    </div>
                                                    <label v-if="alarmGenerationScheduleCustom[day.index].enabled"
                                                           class="flex items-center gap-3 text-sm text-gray-600 cursor-pointer">
                                                        <input :checked="alarmGenerationScheduleCustom[day.index].allDay"
                                                               type="checkbox"
                                                               @change="toggleCustomAllDay(day.index)">
                                                        24 Hours
                                                    </label>
                                                </div>
                                                <div v-if="alarmGenerationScheduleCustom[day.index].enabled && !alarmGenerationScheduleCustom[day.index].allDay"
                                                     class="mt-3 ml-8">
                                                    <div v-for="(interval, index) in alarmGenerationScheduleCustom[day.index].intervals" :key="index"
                                                         class="flex items-center gap-2 mb-2">
                                                        <input :value="formatHhmmReadable(interval.start)"
                                                               class="border p-2 flex-1 text-center rounded-lg cursor-pointer"
                                                               readonly
                                                               type="text"
                                                               @focus="openAlarmGenerationSchedulePicker('start', day.index, index)">
                                                        <span class="text-sm text-gray-500 shrink-0">until</span>
                                                        <input :value="formatHhmmReadable(interval.end)"
                                                               class="border p-2 flex-1 text-center rounded-lg cursor-pointer"
                                                               readonly
                                                               type="text"
                                                               @focus="openAlarmGenerationSchedulePicker('end', day.index, index)">
                                                        <span class="cursor-pointer lnr icon-trash text-gray-400 hover:text-red-500 shrink-0 text-xl p-1"
                                                              @click="removeCustomInterval(day.index, index)"></span>
                                                    </div>
                                                    <button class="mt-1 text-sm text-blue-600 hover:text-blue-500"
                                                            type="button"
                                                            @click="addCustomInterval(day.index)">+ Add Time</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="hidden sm:block">
                <div class="py-5">
                    <div class="border-t border-gray-200"></div>
                </div>
            </div>

            <div class="mt-10 sm:mt-0">
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <div class="px-4 sm:px-0">
                            <span
                                class="text-lg font-medium leading-6 text-gray-900">Alarm Thresholds and Buffer Times</span>
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                Help fight alarm fatigue: set a reasonable alarm buffer time to allow normal variations
                                to occur.
                            </p>
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                We recommend setting your buffer time in accordance with the sensitivity of the
                                product/area being monitored.
                            </p>
                            <p v-if="instantAlarming" class="mt-1 text-sm leading-5 text-gray-600">
                                Instant Alarming allows you to select points that should alarm immediately for <b>safety-critical</b>
                                reasons.
                                If this isn't a safety-critical system, consider using a reasonable alarm buffer time
                                instead.
                            </p>

                            <template v-if="profileCouldCauseAlarm">
                                <div class="bg-yellow-50 border-l-2 border-yellow-400 rounded p-4 mt-2">
                                    <h4 class="flex items-center text-yellow-800 font-semibold text-base m-0">
                                        <span class="lnr icon-warning text-yellow-700 mr-2 !text-base !font-semibold"></span>
                                        Warning
                                    </h4>

                                    <p class="text-sm text-yellow-800 mt-2">
                                        The following points using this alarm profile would currently trigger an alarm based on the defined thresholds.
                                    </p>

                                    <ul class="list-disc list-inside text-sm text-yellow-800 mt-3 ml-1">
                                        <li v-for="meter in this.metersExceeded" :key="meter">
                                            {{ meter }}
                                        </li>
                                    </ul>
                                </div>
                            </template>

                        </div>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="shadow overflow-hidden sm:rounded-md">
                            <div class="px-4 py-5 bg-white sm:p-6">
                                <div class="grid grid-cols-6 gap-6">
                                    <span
                                        class="col-span-6 text-base leading-6 font-medium text-gray-900 border-b border-gray-200">Excursion (Threshold) Alarms</span>

                                    <template
                                        v-for="[valueKey, timeKey, valueText, valueInstantKey] in alarmThresholdVariables">
                                        <div :class='instantAlarming ? "sm:col-span-2" : "sm:col-span-3"'
                                             class="col-span-6">
                                            <div class="w-full flex justify-between -mb-1">
                                                <label class="w-2/3 text-sm font-medium leading-5 text-gray-700 mb-3">
                                                    {{ valueText }}<br/>
                                                    Threshold Limit
                                                </label>
                                                <small-arrow :red="valueKey.includes('alarm')"
                                                             :direction-up="valueKey.includes('high')"></small-arrow>
                                            </div>
                                            <input type="text" :id="valueKey"
                                                   :name="valueKey"
                                                   :value="data[valueKey]"
                                                   @input="debounceInput"
                                                   placeholder="Optional"
                                                   class="mt-1 block w-full py-2 px-3 border border-gray-300 text-center rounded-md shadow-sm focus:outline-none focus:ring-blue-200 focus:border-blue-300 sm:text-base sm:leading-5">
                                        </div>
                                        <div :class='instantAlarming ? "sm:col-span-2" : "sm:col-span-3"'
                                             class="col-span-6">
                                            <div v-if="data.threshold_alarms_enabled">
                                                <label class="block text-sm font-medium leading-5 text-gray-700 mb-3">
                                                    {{ valueText }}<br/>
                                                    Threshold Buffer Time
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder="HH:MM"
                                                    :name="timeKey"
                                                    :id="timeKey"
                                                    :disabled="data[valueInstantKey]"
                                                    :value="data[valueInstantKey] ? 'Instant' : readable[valueKey]"
                                                    class="border p-2 w-full text-center rounded-lg"
                                                    @focus="openModal">
                                            </div>
                                        </div>
                                        <div v-if="instantAlarming"
                                             :class='instantAlarming ? "sm:col-span-2" : "sm:col-span-3"'
                                             class="col-span-6">
                                            <div v-if="data.threshold_alarms_enabled">
                                                <label class="block text-sm font-medium leading-5 text-gray-700 mb-3">
                                                    {{ valueText }}<br/>
                                                    Instant Alarming</label>
                                                <toggle name="high_alarm_instant"
                                                        id="high_alarm_instant"
                                                        v-model:value="data[valueInstantKey]"
                                                ></toggle>
                                            </div>
                                        </div>

                                    </template>
                                    <template v-if="data.offline_alarms_enabled">
                                        <span
                                            class="col-span-6 text-base leading-6 font-medium text-gray-900 border-b border-gray-200">Offline Alarms</span>
                                        <div :class='instantAlarming ? "sm:col-span-2" : "sm:col-span-3"'
                                             class="col-span-6">
                                            <label class="block text-sm font-medium leading-5 text-gray-700">Meter not
                                                transmitting data</label>
                                            <label  class="block text-sm font-medium leading-5 text-gray-700 mt-2">
                                                We recommend a buffer time of at least 01:35 (1 hour & 35 minutes).
                                            </label>
                                        </div>
                                        <div :class='instantAlarming ? "sm:col-span-2" : "sm:col-span-3"'
                                             class="col-span-6">
                                            <div v-if="data.offline_alarms_enabled">
                                                <label for="offline_alarm_time"
                                                       class="block text-sm font-medium leading-5 text-gray-700">Offline
                                                    Alarm Buffer Time</label>
                                                <input type="text"
                                                       placeholder="HH:MM"
                                                       name="offline_alarm_time"
                                                       id="offline_alarm_time"
                                                       :value="offlineAlarmTimeReadable"
                                                       class="border p-2 w-full text-center rounded-lg"
                                                       @focus="openModal">
                                            </div>
                                        </div>
                                    </template>


                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="hidden sm:block">
                <div class="py-5">
                    <div class="border-t border-gray-200"></div>
                </div>
            </div>

            <div    v-if="showAlarmHandler"
                    class="mt-10 sm:mt-0">
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <div class="flex flex-col px-4 sm:px-0 h-full">
                            <span  class="text-lg font-medium leading-6 text-gray-900">
                                Alarm Handler
                            </span>
                            <textarea class="mt-1 text-sm leading-5 text-gray-600 border-0 grow bg-white"
                                        :value="data.alarmHandlerDescriptions[data.handler.handler]"
                                        disabled>
                            </textarea>
                        </div>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="shadow overflow-hidden sm:rounded-md">
                            <div class="px-4 py-5 bg-white sm:p-6">
                                <alarm-handler-select   v-model="data['handler']"
                                                        :handlers="data.alarmHandlers"
                                ></alarm-handler-select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div    v-if="showAlarmHandler"
                    class="hidden sm:block">
                <div class="py-5">
                    <div class="border-t border-gray-200"></div>
                </div>
            </div>

            <div class="mt-10 sm:mt-0 pb-2">
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <div class="px-4 sm:px-0">
                            <span
                                class="text-lg font-medium leading-6 text-gray-900">Alarm Thresholds Visualization</span>
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                Get a feel for how your thresholds are set with this handy chart!
                            </p>
                        </div>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="shadow overflow-hidden sm:rounded-md">
                            <div class="px-4 py-5 bg-white sm:p-6">
                                <alarm-profiles-graph
                                    class=""
                                    v-if="!loading && thresholdsAreNumbers"
                                    :high_alarm_value="data.high_alarm_value"
                                    :high_alarm_instant="data.high_alarm_instant"
                                    :high_alarm_time="highAlarmTimeReadable"
                                    :high_caution_value="data.high_caution_value"
                                    :high_caution_instant="data.high_caution_instant"
                                    :high_caution_time="highCautionTimeReadable"
                                    :low_caution_value="data.low_caution_value"
                                    :low_caution_instant="data.low_caution_instant"
                                    :low_caution_time="lowCautionTimeReadable"
                                    :low_alarm_value="data.low_alarm_value"
                                    :low_alarm_instant="data.low_alarm_instant"
                                    :low_alarm_time="lowAlarmTimeReadable"
                                >

                                </alarm-profiles-graph>
                                <div v-else-if="!thresholdsAreNumbers" class="py-20">
                  <span class="text-center text-base text-gray-500">
                    All Threshold Limits must be Numbers or Blank.  Please check them.
                  </span>
                                </div>
                                <div v-else class="my-20">
                  <span class="text-center text-base text-gray-500">
                    Creating Graph ... Please Wait
                  </span>
                                </div>
                                <div v-if="errors.length > 0">
                                    <error-message>
                                        Errors were encountered while saving this alarm profile:
                                        <li v-for="error in errors">{{ error }}</li>
                                    </error-message>
                                </div>
                            </div>
                            <div v-if="!disableSubmit && this.permissions.can('edit_alarm_profiles') && !viewOnly"
                                 class="px-4 py-3 bg-gray-50 text-right sm:px-6">
                              <p v-if="alarmGenerationScheduleCustomIncomplete" class="mb-2 text-sm text-red-500">
                                  Add at least one time window to the custom schedule, or turn the schedule off.
                              </p>
                              <confirmation-modal @strict-cfr-disabled="submitEvent" @authentication-success="submitEvent">
                                <template v-slot:submitAction="{ onSubmit }">
                                  <button @click.prevent="additionalConfirmation ? handleDisabledClick(onSubmit) : onSubmit()"
                                          id="save-alarm-profile"
                                          :class="alarmGenerationScheduleCustomIncomplete ? 'opacity-50 cursor-not-allowed' : ''"
                                          :disabled="alarmGenerationScheduleCustomIncomplete"
                                          class="w-full py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-blue-200 focus:bg-blue-500 active:bg-blue-600">
                                    Save Alarm Profile
                                  </button>
                                </template>
                              </confirmation-modal>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <hour-minute-picker
                v-model:is-open="showModal.datePicker"
                :seconds="modalSeconds"
                @datePicked="listenForDatepicker"></hour-minute-picker>
            <hour-minute-picker
                v-model:is-open="alarmGenerationSchedulePicker.isOpen"
                :max-seconds="alarmGenerationSchedulePickerMaxSeconds"
                :min-seconds="0"
                :minute-step="15"
                :time="alarmGenerationSchedulePickerTime"
                @datePicked="onAlarmGenerationSchedulePicked"></hour-minute-picker>
        </form>
    </div>
</template>

<script>
import axios from "axios";
import _ from "lodash";
import {DateTime} from 'luxon';

import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import DateTimePicker from "@/components/Reports/VueDatePicker";
import Loading from "@/components/elements/loading";
import AlarmProfilesGraph from "@/components/alarmProfiles/alarmProfilesGraph";
import HourMinutePicker from "@/components/elements/hourMinutePicker";
import ErrorMessage from "@/components/ErrorMessage";
import SmallArrow from "@/components/elements/smallArrow";
import Toggle from "@/components/elements/toggle"
import AlarmHandlerSelect from "@/components/alarmProfiles/AlarmHandlerSelect";
import FloatingInput from "@/components/elements/FloatingInput";

export default {
    name: "alarmProfilesEdit",
    mixins: [SonicuCoreMixin],
    components: {
        FloatingInput,
        AlarmHandlerSelect,
        SmallArrow,
        Toggle,
        ErrorMessage,
        HourMinutePicker,
        AlarmProfilesGraph,
        Loading,
        DateTimePicker, DateTime
    },
    props: {
        id: {
            type: Number,
        },
        instantAlarmingProp: {
            type: Boolean,
            default: false,
        },
        disableSubmit: {
            type: Boolean,
            default: false,
        },
        isAjax: {
            type: Boolean,
            default: false,
        },
        privateMode: {
            type: Boolean,
            default: false,
        },
        defaultName: {
            type: String,
            default: null,
        },
        viewOnly: {
            type: Boolean,
            default: false,
        },
        timezoneAbbreviated: {
            type: String,
            required: true,
        },
    },
    mounted() {
    },
    data() {
        return {
            data: {
                force_push_indicators: false,
                threshold_alarms_enabled: false,
            },
            showModal: {
                profileCouldCauseAlarm: false,
                datePicker: false
            },
            showModalEvent: null,
            modalSeconds: null,
            loading: true,
            instantAlarming: this.instantAlarmingProp,
            errors: [],
            showPrivateToggle: this.privateMode,
            alarmThresholdVariables: [
                ['high_alarm_value', 'high_alarm_time', 'High Alarm', 'high_alarm_instant'],
                ['high_caution_value', 'high_caution_time', 'High Caution', 'high_caution_instant'],
                ['low_caution_value', 'low_caution_time', 'Low Caution', 'low_caution_instant'],
                ['low_alarm_value', 'low_alarm_time', 'Low Alarm', 'low_alarm_instant'],
            ],
            handler: {},
            alarmHandlers: {},
            alarmGenerationScheduleEnabled: false,
            alarmGenerationScheduleMode: 'daily',
            alarmGenerationScheduleStart: '0800',
            alarmGenerationScheduleEnd: '1700',
            // Custom mode: per-day windows keyed by Carbon dayOfWeek int (0=Sun..6=Sat).
            alarmGenerationScheduleCustom: {
                0: {enabled: false, allDay: false, intervals: []},
                1: {enabled: false, allDay: false, intervals: []},
                2: {enabled: false, allDay: false, intervals: []},
                3: {enabled: false, allDay: false, intervals: []},
                4: {enabled: false, allDay: false, intervals: []},
                5: {enabled: false, allDay: false, intervals: []},
                6: {enabled: false, allDay: false, intervals: []},
            },
            alarmGenerationScheduleDays: [
                {index: 1, label: 'Monday'},
                {index: 2, label: 'Tuesday'},
                {index: 3, label: 'Wednesday'},
                {index: 4, label: 'Thursday'},
                {index: 5, label: 'Friday'},
                {index: 6, label: 'Saturday'},
                {index: 0, label: 'Sunday'},
            ],
            alarmGenerationSchedulePicker: {
                isOpen: false,
                field: null,
                day: null,
                index: null,
            },
            highAlarmExceededProfile: false,
            lowAlarmExceededProfile: false,
            metersExceeded: [],
            inputChanged: false,
            additionalConfirmation: false,
            handleSubmit: null,
            userAllowed: {
                profileCouldCauseAlarm: false,
            },
        }
    },
    created() {
        this.getData();
    },
    computed: {
        // True when the schedule is on and in Custom mode but no day yields a usable window. Blocks
        // Save so an empty Custom schedule can't silently persist as null and fall back to 24/7.
        alarmGenerationScheduleCustomIncomplete() {
            if (!this.alarmGenerationScheduleEnabled || this.alarmGenerationScheduleMode !== 'custom') {
                return false;
            }
            return this.buildCustomScheduleBlocks() === null;
        },
        readable() {
            return {
                'high_alarm_value': this.highAlarmTimeReadable,
                'high_caution_value': this.highCautionTimeReadable,
                'low_caution_value': this.lowCautionTimeReadable,
                'low_alarm_value': this.lowAlarmTimeReadable,
            }
        },
        hasSoundDevices() {
            if (!('has_sound_devices' in this.data)) {
                return false
            }
            return this.data.has_sound_devices
        },
        highAlarmTimeReadable() {
            if (_.isNil(this.data.high_alarm_time)) {
                return '';
            }
            let minutes = this.data.high_alarm_time / 60;
            let hours = Math.floor(minutes / 60);
            minutes = minutes % 60;
            return hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0");
        },
        highCautionTimeReadable() {
            if (_.isNil(this.data.high_caution_time)) {
                return '';
            }
            let minutes = this.data.high_caution_time / 60;
            let hours = Math.floor(minutes / 60);
            minutes = minutes % 60;
            return hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0");
        },
        lowCautionTimeReadable() {
            if (_.isNil(this.data.low_caution_time)) {
                return '';
            }
            let minutes = this.data.low_caution_time / 60;
            let hours = Math.floor(minutes / 60);
            minutes = minutes % 60;
            return hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0");
        },
        lowAlarmTimeReadable() {
            if (_.isNil(this.data.low_alarm_time)) {
                return '';
            }
            let minutes = this.data.low_alarm_time / 60;
            let hours = Math.floor(minutes / 60);
            minutes = minutes % 60;
            return hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0");
        },
        offlineAlarmTimeReadable() {
            if (_.isNil(this.data.offline_alarm_time)) {
                return '01:35';
            }
            let minutes = this.data.offline_alarm_time / 60;
            let hours = Math.floor(minutes / 60);
            minutes = minutes % 60;
            return hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0");
        },
        thresholdResolveTimeReadable() {
            if (_.isNil(this.data.threshold_resolve_time)) {
                return '';
            }
            let minutes = this.data.threshold_resolve_time / 60;
            let hours = Math.floor(minutes / 60);
            minutes = minutes % 60;
            return hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0");
        },
        offlineTimeValid() {
            // If offline is not set then consider it valid
            if (_.isNil(this.data.offline_alarm_time)) {
                return true
            }
            // if it is a sonicu user, also hop out
            if (this.permissions.isSonicu()) {
                return true
            }
            // Make sure that the offline time is at least 35 minutes.
            return this.data.offline_alarm_time / 60 >= 35
        },
        thresholdsAreNumbers() {
            return this.validateThresholdIsNullBlankOrNumber(this.data.high_alarm_value) &&
                this.validateThresholdIsNullBlankOrNumber(this.data.high_caution_value) &&
                this.validateThresholdIsNullBlankOrNumber(this.data.low_caution_value) &&
                this.validateThresholdIsNullBlankOrNumber(this.data.low_alarm_value)
        },
        formRoute() {
            if (_.isNil(this.id)) {
                return this.company.buildCompanyApiInternalRoute('alarm-profiles/');
            } else {
                return this.company.buildCompanyApiInternalRoute('alarm-profiles/' + this.id);
            }
        },
        formMethod() {
            if (_.isNil(this.id)) {
                return 'post';
            } else {
                return 'patch';
            }
        },
        showAlarmHandler() {
            return this.data.alarmHandlers && Object.keys(this.data.alarmHandlers).length > 1
        },
        alarmGenerationScheduleStartReadable() {
            return this.formatHhmmReadable(this.alarmGenerationScheduleStart);
        },
        alarmGenerationScheduleEndReadable() {
            return this.formatHhmmReadable(this.alarmGenerationScheduleEnd);
        },
        // An end time may go up to 24:00 (86400s, the end-of-day sentinel "2400"); a start time is
        // capped at 23:45 since a block starting at 2400 is nonsensical and the model drops it.
        alarmGenerationSchedulePickerMaxSeconds() {
            return this.alarmGenerationSchedulePicker.field === 'end' ? 86400 : 85500;
        },
        // The HH:MM string fed into the picker for whichever field is currently being edited.
        alarmGenerationSchedulePickerTime() {
            const picker = this.alarmGenerationSchedulePicker;
            if (picker.field === null) return null;

            // Custom mode targets a specific day + interval.
            if (picker.day !== null) {
                const interval = this.alarmGenerationScheduleCustom[picker.day].intervals[picker.index];
                return this.formatHhmmReadable(interval[picker.field]);
            }

            if (picker.field === 'start') return this.alarmGenerationScheduleStartReadable;
            if (picker.field === 'end') return this.alarmGenerationScheduleEndReadable;
            return null;
        },
        profileCouldCauseAlarm() {
            if (this.metersExceeded.length > 5) {
                this.additionalConfirmation = true;
            }

            return this.metersExceeded.length > 0;
        },
    },
    methods: {
        getData() {
            let route = this.id === null
                ? this.company.buildCompanyApiInternalRoute('alarm-profiles/create')
                : this.company.buildCompanyApiInternalRoute('alarm-profiles/' + this.id);
            axios.get(route).then(
                response => {
                    this.data = response.data;
                    if (this.privateMode) {
                        this.data.private = true;
                    }
                    if (this.data.private) {
                        this.showPrivateToggle = true;
                    }
                    if (this.defaultName) {
                        this.data.name = this.defaultName;
                    }

                    this.data.handler = this.data.alarm_handler_data

                    this.loadAlarmGenerationSchedule(this.data.alarm_generation_schedule);

                    // Set the modalSeconds based on a default time if offline alarm time is not set.

                    this.loading = false;
                }
            ).catch(
                error => {

                }
            )
        },
        debounceInput: _.debounce(function (e) {
            this.data[e.srcElement.id] = e.target.value;

            if (this.inputChanged || e.srcElement.id.includes('alarm')) {
                this.inputChanged = true;
                this.updateCouldAlarmStatus()
            }
        }, 500),
        openModal(e) {
            this.showModalEvent = e;

            // Get modalSeconds from the input.  Defaulting to values based on key
            let elementId = e.srcElement.id
            if (elementId === 'offline_alarm_time') {
                this.modalSeconds = _.isNil(this.data.offline_alarm_time)
                    ? "" + 60 * 95 + ""
                    : this.data.offline_alarm_time
            }
            else {
                this.modalSeconds = this.data[e.srcElement.id]
            }

            // Parse the string seconds into an integer
            this.modalSeconds = parseInt(this.modalSeconds);
            this.showModal.datePicker = true;
        },
        listenForDatepicker(payload) {
            // payload is null on Reset, otherwise an object with { seconds, hours, minutes, time, hhmm }.
            const seconds = payload === null ? null : payload.seconds;

            // We are going to have an exception for offline alarm times, they if reset, should go to 95 minutes.
            if (this.showModalEvent.srcElement.id === 'offline_alarm_time' && _.isNil(seconds)) {
                this.data.offline_alarm_time = 60 * 95
            } else {
                // All other values should be set to the value of the modal.
                this.data[this.showModalEvent.srcElement.id] = seconds;
            }

            // Hide the modal
            this.showModalEvent = null;
        },
        submitEvent(confirmationRequestUuid = null) {
            if (this.profileCouldCauseAlarm && ! this.userAllowed.profileCouldCauseAlarm) {
                this.showModal.profileCouldCauseAlarm = true;

                return;
            }

            this.errors = []
            // Cancel the save if the offline time is less than 35 minutes.
            // TODO consider having this set the offline time to 35 minutes for the user to hit save again.
            if (this.data.offline_alarms_enabled && !this.offlineTimeValid) {
                this.errors = ['Offline time must be at least 35 minutes.']
                return
            }

            if (!this.validateThresholdValues()) {
                return
            }

            if (this.alarmGenerationScheduleEnabled) {
                if (this.alarmGenerationScheduleMode === 'custom') {
                    const customBlocks = this.buildCustomScheduleBlocks()
                    if (customBlocks === null) {
                        this.errors = ['Add at least one time window to the custom schedule, or turn the schedule off.']
                        return
                    }
                    this.data.alarm_generation_schedule = customBlocks
                } else {
                    if (this.alarmGenerationScheduleStart === this.alarmGenerationScheduleEnd) {
                        this.errors = ['Alarm generation schedule start and end cannot be the same.']
                        return
                    }
                    const block = {start: this.alarmGenerationScheduleStart, end: this.alarmGenerationScheduleEnd}
                    if (this.alarmGenerationScheduleMode === 'weekday') {
                        // Weekday mode always applies Monday–Friday (Carbon dayOfWeek 1–5).
                        block.days = [1, 2, 3, 4, 5]
                    }
                    this.data.alarm_generation_schedule = [block]
                }
            } else {
                this.data.alarm_generation_schedule = null
            }

            this.data['confirmation_request_uuid'] = confirmationRequestUuid;
            axios({'method': this.formMethod, 'url': this.formRoute, 'data': this.data}).then(
                response => {
                    // Reset the acknowledgment latch so a later edit re-warns.
                    this.userAllowed.profileCouldCauseAlarm = false;
                    if (this.isAjax) {  // Emit that it was saved
                        this.$emit('profileSaved', response);
                    } else {
                        window.location.href = this.company.buildCompanyRoute('alarm-profiles');
                    }
                }
            ).catch(
                error => {
                    this.errors = error.response.data.errors;
                }
            )
        },
        validateThresholdValues() {
            if (! this.validateThresholdsAreNumbers()) {
                this.errors.push('Some Thresholds are not Numbers')
                return false
            }

            let thresholdRankings = {
                high_alarm_value: 4,
                high_caution_value: 3,
                low_caution_value: 2,
                low_alarm_value: 1,
            }
            let valid = true


            Object.keys(thresholdRankings).forEach(highKey => {
                let high = this.data[highKey]
                if (_.isNil(high)) {
                    return
                }
                Object.keys(thresholdRankings).forEach(compKey => {
                    if (!_.isNil(this.data[compKey]) && thresholdRankings[compKey] < thresholdRankings[highKey]) {
                        if (this.data[compKey] > high) {
                            valid = false
                            this.errors.push(`${compKey} value cannot be higher than ${highKey}`)
                        }
                    }
                })
            })

            return valid
        },
        validateThresholdsAreNumbers() {
            let allNumbers = true
            Object.keys(this.readable).forEach(key => {
                if (_.isNil(this.data[key]) || this.data[key] === '') {
                    this.data[key] = null
                    return
                }
                let tmp = parseFloat(`${this.data[key]}`)
                if (_.isNaN(tmp)) {
                    allNumbers = false
                    return
                }
                this.data[key] = tmp
            })
            return allNumbers
        },
        validateThresholdIsNullBlankOrNumber(threshold) {
            if (_.isNil(threshold) || threshold === '') {
                // We are good because it is blank or null
                return true
            }

            // Test if the threshold can be converted to a float.
            let tmp = parseFloat(`${threshold}`)

            // If the value is not a number, then it is not valid. (isNumber causes recursion)
            return ! _.isNaN(tmp)
        },
        // Opens the shared hourMinutePicker. Daily/Weekday pass just a field; Custom also passes the
        // target day (Carbon dayOfWeek int) and interval index.
        openAlarmGenerationSchedulePicker(field, day = null, index = null) {
            this.alarmGenerationSchedulePicker.field = field;
            this.alarmGenerationSchedulePicker.day = day;
            this.alarmGenerationSchedulePicker.index = index;
            this.alarmGenerationSchedulePicker.isOpen = true;
        },
        onAlarmGenerationSchedulePicked(payload) {
            // On Reset, payload is null and the active field time should be set to default.
            //   Otherwise, it should be set to the new value
            let isResetPayload = payload === null
            const picker = this.alarmGenerationSchedulePicker;
            const defaults = {start: '0800', end: '1700'};

            if (picker.day !== null) {
                // Custom mode: write the specific day/interval.
                const interval = this.alarmGenerationScheduleCustom[picker.day].intervals[picker.index];
                interval[picker.field] = isResetPayload ? defaults[picker.field] : payload.hhmm;
            } else if (picker.field === 'start') {
                this.alarmGenerationScheduleStart = isResetPayload ? defaults.start : payload.hhmm;
            } else if (picker.field === 'end') {
                this.alarmGenerationScheduleEnd = isResetPayload ? defaults.end : payload.hhmm;
            }

            picker.field = null;
            picker.day = null;
            picker.index = null;
        },
        formatHhmmReadable(hhmm) {
            const s = String(hhmm || '').padStart(4, '0');
            // Full-day blocks end at 2400 (midnight); show it as such rather than "24:00".
            if (s === '2400') return '24:00';
            return `${s.slice(0, 2)}:${s.slice(2, 4)}`;
        },
        // ---- Custom schedule helpers ----
        buildDefaultCustomSchedule() {
            const schedule = {};
            for (let day = 0; day <= 6; day++) {
                schedule[day] = {enabled: false, allDay: false, intervals: []};
            }
            return schedule;
        },
        loadAlarmGenerationSchedule(schedule) {
            if (!Array.isArray(schedule) || schedule.length === 0) {
                this.alarmGenerationScheduleEnabled = false;
                this.alarmGenerationScheduleMode = 'daily';
                return;
            }

            this.alarmGenerationScheduleEnabled = true;
            const first = schedule[0];
            const firstDays = Array.isArray(first.days) ? first.days : [];

            // A single dayless block is Daily; a single Mon–Fri block is Weekday; anything else is Custom.
            const isWeekday = schedule.length === 1
                && firstDays.length === 5
                && [1, 2, 3, 4, 5].every(d => firstDays.includes(d));

            if (schedule.length === 1 && firstDays.length === 0) {
                this.alarmGenerationScheduleMode = 'daily';
                this.alarmGenerationScheduleStart = first.start;
                this.alarmGenerationScheduleEnd = first.end;
            } else if (isWeekday) {
                this.alarmGenerationScheduleMode = 'weekday';
                this.alarmGenerationScheduleStart = first.start;
                this.alarmGenerationScheduleEnd = first.end;
            } else {
                this.alarmGenerationScheduleMode = 'custom';
                const custom = this.buildDefaultCustomSchedule();
                schedule.forEach(block => {
                    const days = (Array.isArray(block.days) && block.days.length > 0) ? block.days : [0, 1, 2, 3, 4, 5, 6];
                    const isFullDay = String(block.start) === '0000' && String(block.end) === '2400';
                    days.forEach(day => {
                        if (custom[day] === undefined) return;
                        custom[day].enabled = true;
                        if (isFullDay) {
                            custom[day].allDay = true;
                        } else {
                            custom[day].intervals.push({start: block.start, end: block.end});
                        }
                    });
                });
                this.alarmGenerationScheduleCustom = custom;
            }
        },
        buildCustomScheduleBlocks() {
            // Collect each enabled day's set of windows (sorted for a stable signature).
            const dayWindows = {};
            for (let day = 0; day <= 6; day++) {
                const config = this.alarmGenerationScheduleCustom[day];
                if (!config.enabled) continue;

                const windows = config.allDay
                    ? [{start: '0000', end: '2400'}]
                    : config.intervals
                        .filter(interval => interval.start !== interval.end)
                        .map(interval => ({start: interval.start, end: interval.end}));

                if (windows.length === 0) continue;
                windows.sort((a, b) => a.start.localeCompare(b.start));
                dayWindows[day] = windows;
            }

            // Merge days that share an identical window-set into a single block per window,
            // tagged with every matching day (e.g. Mon–Fri with the same hours stores as
            // days:[1,2,3,4,5] instead of five copies). Keys iterate in ascending day order.
            const groups = {};
            Object.keys(dayWindows).forEach(day => {
                const windows = dayWindows[day];
                const signature = windows.map(w => `${w.start}-${w.end}`).join('|');
                groups[signature] ??= {days: [], windows};
                groups[signature].days.push(parseInt(day, 10));
            });

            const blocks = [];
            Object.values(groups).forEach(group => {
                group.windows.forEach(window => {
                    blocks.push({start: window.start, end: window.end, days: group.days});
                });
            });

            return blocks.length > 0 ? blocks : null;
        },
        toggleCustomDay(day) {
            // The toggle's v-model has already updated `enabled`; just seed a sensible default
            // interval when a day is switched on with nothing configured yet.
            const config = this.alarmGenerationScheduleCustom[day];
            if (config.enabled && !config.allDay && config.intervals.length === 0) {
                config.intervals.push({start: '0800', end: '1700'});
            }
        },
        toggleCustomAllDay(day) {
            const config = this.alarmGenerationScheduleCustom[day];
            config.allDay = !config.allDay;
            if (!config.allDay && config.intervals.length === 0) {
                config.intervals.push({start: '0800', end: '1700'});
            }
        },
        addCustomInterval(day) {
            this.alarmGenerationScheduleCustom[day].intervals.push({start: '0800', end: '1700'});
        },
        removeCustomInterval(day, index) {
            const config = this.alarmGenerationScheduleCustom[day];
            config.intervals.splice(index, 1);
            // A day with no remaining intervals turns itself off.
            if (config.intervals.length === 0 && !config.allDay) {
                config.enabled = false;
            }
        },
        updateEnableOfflineAlarmThreshold(enabled) {
            // This is to ensure that the offline alarm time is set to a sensable default
            // If the offline alarm is being enabled & the offline alarm time is not set, set it to 95 minutes.
            if (enabled && _.isNil(this.data.offline_alarm_time)) {
                this.data.offline_alarm_time = 60 * 95
            }
        },
        updateCouldAlarmStatus() {
            if (! this.inputChanged) {
                return;
            }
            this.metersExceeded = [];

            const highAlarm = this.data.high_alarm_value != null && this.data.high_alarm_value !== ''
                ? Number(this.data.high_alarm_value)
                : null;

            const lowAlarm = this.data.low_alarm_value != null && this.data.low_alarm_value !== ''
                ? Number(this.data.low_alarm_value)
                : null;

            const alarmsEnabled = this.data.threshold_alarms_enabled;

            // The create endpoint does not append last_readings (a new profile has no meters),
            // so default to an empty list instead of throwing inside the watcher.
            const lastReadings = this.data.last_readings ?? [];

            if (lastReadings.length === 0) {
                return;
            }

            lastReadings.forEach((meter) => {
                if (alarmsEnabled && meter.reading !== null && highAlarm !== null && meter.reading >= highAlarm) {
                    this.metersExceeded.push(meter.name);
                }

                if (alarmsEnabled && meter.reading !== null && lowAlarm !== null && meter.reading <= lowAlarm) {
                    this.metersExceeded.push(meter.name);
                }
            })
        },
        handleDisabledClick(onSubmit) {
            this.showModal.profileCouldCauseAlarm = true;
            this.handleSubmit = onSubmit;
        },
        confirmProfileCouldCauseAlarm() {
            // User acknowledged the warning: latch it so submitEvent no longer
            // re-opens the modal, close the modal, then run the pending submit.
            this.userAllowed.profileCouldCauseAlarm = true;
            this.showModal.profileCouldCauseAlarm = false;

            // handleSubmit is set only via the additionalConfirmation (>5 meters)
            // path; fall back to submitEvent directly for the standard path.
            if (this.handleSubmit) {
                this.handleSubmit();
                this.handleSubmit = null;
            } else {
                this.submitEvent();
            }
        }
    },
    watch: {
        id: function (val) {
            this.getData();
        },
        'data.high_alarm_value': function() {
            this.updateCouldAlarmStatus();
        },
        'data.low_alarm_value': function() {
            this.updateCouldAlarmStatus();
        },
        'data.threshold_alarms_enabled': function() {
            this.updateCouldAlarmStatus();
        }
    }
}
</script>

<style scoped>

</style>